import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, X } from 'lucide-react';

// Global FOMO purchase feed — names + cities from around the world (ads run worldwide)
// addons values map onto the 5 real add-ons in orderBumps
const feed = [
  { name: 'Sophia R.', city: 'London, UK', addons: 0, ago: '2 min ago' },
  { name: 'Liam T.', city: 'New York, USA', addons: 2, ago: '4 min ago' },
  { name: 'Yuki M.', city: 'Tokyo, Japan', addons: 5, ago: '7 min ago' },
  { name: 'Emma S.', city: 'Sydney, Australia', addons: 1, ago: '11 min ago' },
  { name: 'Diego P.', city: 'Mexico City, Mexico', addons: 3, ago: '15 min ago' },
  { name: 'Chloé D.', city: 'Paris, France', addons: 5, ago: '19 min ago' },
  { name: 'David K.', city: 'Toronto, Canada', addons: 2, ago: '23 min ago' },
  { name: 'Ines M.', city: 'Barcelona, Spain', addons: 0, ago: '27 min ago' },
  { name: 'Amir H.', city: 'Dubai, UAE', addons: 5, ago: '31 min ago' },
  { name: 'Nadia B.', city: 'Berlin, Germany', addons: 3, ago: '36 min ago' },
  { name: 'Priya S.', city: 'Mumbai, India', addons: 1, ago: '41 min ago' },
  { name: 'Lucas F.', city: 'São Paulo, Brazil', addons: 4, ago: '47 min ago' },
  { name: 'Freya J.', city: 'Amsterdam, Netherlands', addons: 3, ago: '52 min ago' },
  { name: 'Kagiso M.', city: 'Cape Town, South Africa', addons: 2, ago: '58 min ago' },
  { name: 'Elena V.', city: 'Rome, Italy', addons: 5, ago: '1 hr ago' },
  { name: 'Noah W.', city: 'Los Angeles, USA', addons: 3, ago: '1 hr ago' },
  { name: 'Aisha R.', city: 'Cairo, Egypt', addons: 1, ago: '1 hr ago' },
  { name: 'Mateo G.', city: 'Buenos Aires, Argentina', addons: 5, ago: '2 hrs ago' },
];

const labelFor = (addons) => {
  if (addons === 0) return 'The Supreme Awakening Collection';
  if (addons === 5) return 'Collection + All 5 Add-Ons';
  return `Collection + ${addons} Add-On${addons > 1 ? 's' : ''}`;
};

const badgeColor = (addons) => {
  if (addons === 0) return 'bg-emerald-500';
  if (addons <= 2) return 'bg-amber-500';
  if (addons <= 4) return 'bg-gradient-to-r from-orange-500 to-amber-500';
  return 'bg-gradient-to-r from-rose-500 via-orange-500 to-amber-400';
};

export default function MeditationPurchaseNotifier({ variant = 'landing' }) {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const timers = useRef([]);

  // Filter feed based on variant - checkout emphasizes addon buyers
  const list =
    variant === 'checkout'
      ? feed.filter((f) => f.addons > 0).concat(feed.filter((f) => f.addons > 0)).slice(0, 12)
      : feed;

  useEffect(() => {
    // First popup appears after 3.5s. Start the rotation timer only AFTER
    // the first popup has been visible for ~7s so each notification stays
    // on-screen for the full interval.
    const start = setTimeout(() => {
      setVisible(true);
      const cycle = setInterval(() => {
        setVisible(false);
        const t = setTimeout(() => {
          setIdx((i) => (i + 1) % list.length);
          setVisible(true);
        }, 500);
        timers.current.push(t);
      }, 9000);
      timers.current.push(cycle);
    }, 10000);
    timers.current.push(start);

    return () => {
      timers.current.forEach((t) => {
        clearTimeout(t);
        clearInterval(t);
      });
    };
    // eslint-disable-next-line
  }, []);

  if (dismissed) return null;

  const n = list[idx];

  return (
    <div
      data-testid="purchase-notifier"
      className={`fixed left-3 md:left-6 bottom-3 md:bottom-6 z-40 max-w-[92vw] md:max-w-none transition-all duration-500 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      <div className="relative flex items-center gap-3 bg-white rounded-xl shadow-2xl border border-slate-200 pl-3 pr-7 py-2.5 min-w-[240px] max-w-[320px]">
        <div className={`w-9 h-9 rounded-full ${badgeColor(n.addons)} flex items-center justify-center flex-shrink-0`}>
          <CheckCircle2 className="text-white" size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-bold text-slate-900 truncate">
            {n.name} from {n.city}
          </div>
          <div className="text-[11px] text-slate-600 leading-tight truncate">
            purchased <span className="font-semibold text-slate-800">{labelFor(n.addons)}</span>
          </div>
          <div className="text-[10px] text-slate-400 leading-tight">{n.ago}</div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="absolute top-1.5 right-1.5 text-slate-400 hover:text-slate-600"
        >
          <X size={11} />
        </button>
      </div>
    </div>
  );
}
